import { Drawer, Loading } from '@/components';
import {
  useSubProjectsForKS,
  useUpdateSubProjectFundBusiness,
} from '@/features/project/_hooks/sub-project';
import type { SubProjectUpdateFundBusinessFormSchema } from '@/features/project/projects/_models/sub-project';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { UpdateSubProjectIncomeAndExpenditureAndFundBusinessForm } from './UpdateSubProjectIncomeAndExpenditureAndFundBusinessForm';

type Props = {
  fiscalYear: number;
};

export const UpdateSubProjectIncomeAndExpenditureAndFundBusinessFormDrawer: FC<
  Props
> = ({ fiscalYear }) => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { id: projectId } = useId();
  const subProjects = useSubProjectsForKS(projectId);
  const { openMessage } = useMessage();

  const isOpen =
    drawerState.open &&
    drawerState.type === 'execution-progress' &&
    drawerState.formType === 'individual-table';

  const subProject = isOpen
    ? subProjects.data?.find((sp) => sp.id === drawerState.subProjectId)
    : undefined;

  const defaultActiveKey = isOpen ? drawerState.target : undefined;

  const closeDrawer = () => {
    setDrawerState({ open: false });
  };

  const { mutateAsync: updateSubProjectFundBusiness } =
    useUpdateSubProjectFundBusiness(projectId);

  const handleSubmit = async (
    values: z.infer<typeof SubProjectUpdateFundBusinessFormSchema>,
  ) => {
    invariant(isOpen, t('unreachable-code'));

    try {
      await updateSubProjectFundBusiness({
        subProjectId: drawerState.subProjectId,
        fundBusinessForLocalGovernment: values,
      });
      await Promise.all([
        openMessage('success', t('message-update-completed')),
        (async () => {
          closeDrawer();
        })(),
      ]);
    } catch {
      await openMessage('error', t('message-update-failed'));
    }
  };

  return (
    <Drawer
      size="large"
      destroyOnClose
      open={isOpen}
      onClose={closeDrawer}
      title={t('edit-income-and-expenditure-and-fund-business')}
    >
      {subProjects.isPending ? (
        <Loading />
      ) : (
        <UpdateSubProjectIncomeAndExpenditureAndFundBusinessForm
          fiscalYear={fiscalYear}
          initialValues={
            subProject?.fundBusinessForLocalGovernment ?? undefined
          }
          onSubmit={handleSubmit}
          defaultActiveKey={defaultActiveKey}
        />
      )}
    </Drawer>
  );
};
